export class CookieService {
    /**
     * Set a cookie with the specified name, value, and options
     * @param name - Cookie name
     * @param value - Cookie value
     * @param options - Cookie options (expires, path, domain, secure, sameSite)
     */
    public static setCookie(
        name: string,
        value: string,
        options: {
            expires?: Date | number;
            path?: string;
            domain?: string;
            secure?: boolean;
            sameSite?: 'strict' | 'lax' | 'none';
        } = {}
    ): void {
        if (typeof document === 'undefined') {
            return;
        }

        let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        if (options.expires) {
            const expiresDate = typeof options.expires === 'number' 
                ? new Date(Date.now() + options.expires * 24 * 60 * 60 * 1000)
                : options.expires;
            cookieString += `; expires=${expiresDate.toUTCString()}`;
        }

        if (options.path) {
            cookieString += `; path=${options.path}`;
        }

        if (options.domain) {
            cookieString += `; domain=${options.domain}`;
        }

        if (options.secure) {
            cookieString += '; secure';
        }

        if (options.sameSite) {
            cookieString += `; samesite=${options.sameSite}`;
        }

        document.cookie = cookieString;
    }

    /**
     * Get a cookie value by name
     * @param name - Cookie name
     * @returns Cookie value or null if not found
     */
    public static getCookie(name: string): string | null {
        if (typeof document === 'undefined') {
            return null;
        }

        const nameEQ = `${encodeURIComponent(name)}=`;
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1, cookie.length);
            }
            if (cookie.indexOf(nameEQ) === 0) {
                return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
            }
        }

        return null;
    }

    /**
     * Remove a cookie by setting its expiration date to the past
     * @param name - Cookie name
     * @param options - Cookie options (path, domain) - must match the options used when setting the cookie
     */
    public static removeCookie(
        name: string,
        options: {
            path?: string;
            domain?: string;
        } = {}
    ): void {
        if (typeof document === 'undefined') {
            return;
        }

        // Set the cookie to expire in the past
        this.setCookie(name, '', {
            expires: new Date(0),
            path: options.path,
            domain: options.domain
        });
    }

    /**
     * Check if a cookie exists
     * @param name - Cookie name
     * @returns True if cookie exists, false otherwise
     */
    public static hasCookie(name: string): boolean {
        return this.getCookie(name) !== null;
    }

    /**
     * Get all cookies as an object
     * @returns Object with cookie name-value pairs
     */
    public static getAllCookies(): Record<string, string> {
        if (typeof document === 'undefined') {
            return {};
        }

        const cookies: Record<string, string> = {};
        const cookieString = document.cookie;

        if (!cookieString) {
            return cookies;
        }

        cookieString.split(';').forEach(cookie => {
            const [name, value] = cookie.trim().split('=');
            if (name && value) {
                try {
                    cookies[decodeURIComponent(name)] = decodeURIComponent(value);
                } catch {
                    // Fallback for malformed cookies
                    cookies[name] = value;
                }
            }
        });

        return cookies;
    }
}
